import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import ReportBody from './ReportBody'
import ReportToolbar from './ReportToolbar'
import useActivityReport from './useActivityReport'
import { DEFAULT_EXCLUDED_USERS, filterRows } from './activityReport'
import { getReport } from './reportRegistry'
import './Reports.css'

// Last 14 days, ending today (inclusive on both ends, so "14 days" means 14
// calendar days shown, not 14 days back from yesterday).
const defaultRange = () => ({
    startDate: moment().subtract(13, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
})

// The panel's starting filter state -- every dimension empty except
// exclude-users, which starts with System and cloud_functions already
// excluded (see DEFAULT_EXCLUDED_USERS in activityReport.js for why that is a
// static value here rather than something an effect seeds in once rows
// arrive). Those two tokens are ordinary, removable toolbar tokens from the
// first render on; nothing re-adds them once a caller's own `filters` state
// no longer has them.
const DEFAULT_FILTERS = { users: [], branches: [], groups: [], actions: [], excludeUsers: DEFAULT_EXCLUDED_USERS }

/**
 * The embeddable unit a listing page drops in to get that module's activity
 * log as a toggle, in place of the old standalone `/reports` page. One
 * `reportId` per mounted panel: a listing page only ever shows its own
 * report, so nothing here branches on which report it is -- every report is
 * driven off `reportRegistry.js`.
 *
 * Two tabs cover what a report needs: the module's own subject (labelled
 * from `report.name`) and the shared Users view (`ReportBody`'s `layout`
 * prop, `'module'` vs `'users'`). That is a display concern only -- the date
 * range and every filter token scope both layouts identically, so switching
 * tabs never resets them. The one thing that does reset filters is the
 * report itself changing under a mounted panel: options are per-report, so a
 * filter selected on one report could silently narrow everything away on
 * another. `ReportToolbar` remounts (`key={reportId}`) for the same reason --
 * its revealed-token state is local and independent of the filter values
 * themselves.
 *
 * The panel's own heading and subheading sit above the tabs, labelling this
 * panel -- a listing page's own page-level heading, if it has one, lives
 * outside this component entirely. The heading pairs the report's name with
 * "activity log" (not "report"), a history icon, and a subtitle that says
 * outright these are counts of recorded create/edit events read back from the
 * history log, not live totals off the record itself -- everything on this
 * panel is one BigQuery query over `bos_models_history_v2`, and the framing
 * exists so a viewer never mistakes it for a query over live business data.
 * The tabs are plain `role="tab"` buttons rather than
 * the shared `ListingTabs` component: this switch is subordinate to a
 * listing page's own primary navigation (see Reports.css), and reusing
 * ListingTabs here would make it read as a second, competing rail of the
 * same kind of control.
 */
const ReportsPanel = ({ reportId }) => {
    const [range, setRange] = useState(defaultRange)
    const [view, setView] = useState('chart')
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [activeTab, setActiveTab] = useState('module')

    const report = getReport(reportId)

    useEffect(() => {
        setFilters(DEFAULT_FILTERS)
    }, [reportId])

    // The user/branch/group/exclude-user filters go to the hook; the action
    // filter is applied here, because it is a display concern and the hook
    // derives its dropdown options from the unfiltered rows.
    //
    // A group expands to its members' ids, so several groups union together and
    // then intersect with whatever else is selected. `excludeUserIds` is its
    // own dimension rather than folded into `userIds` -- see `filterRows`'s
    // docstring for why an intersecting exclude pass is what makes exclusion
    // win over inclusion for the same person.
    //
    // The exclude-users token holds both id-matched entries (System, and any
    // person picked from the dropdown) and the one username-matched entry
    // (the `cloud_functions` default -- see DEFAULT_EXCLUDED_USERS), tagged by
    // `matchBy`. They are split into filterRows's two separate dimensions
    // here, at the one point that already knows the token shape.
    const rowFilters = useMemo(
        () => ({
            users: filters.users.map((item) => item.value),
            branches: filters.branches.map((item) => item.value),
            userIds: [...new Set(filters.groups.flatMap((group) => group.userIds || []))],
            excludeUserIds: filters.excludeUsers
                .filter((item) => item.matchBy !== 'username')
                .map((item) => item.value),
            excludeUsernames: filters.excludeUsers
                .filter((item) => item.matchBy === 'username')
                .map((item) => item.value),
        }),
        [filters]
    )

    const {
        loading,
        error,
        filteredRows,
        userOptions,
        branchOptions,
        groupOptions,
        excludeUserOptions,
        canViewAllUsers,
        catalogLoading,
    } = useActivityReport({
        reportId,
        startDate: range.startDate,
        endDate: range.endDate,
        filters: rowFilters,
    })

    const actionValues = useMemo(() => filters.actions.map((item) => item.value), [filters.actions])
    const visibleRows = useMemo(
        () => filterRows(filteredRows, { actions: actionValues }),
        [filteredRows, actionValues]
    )

    const options = useMemo(
        () => ({
            users: userOptions,
            branches: branchOptions,
            groups: groupOptions,
            actions: report.actions.map((action) => ({ value: action.value, label: action.label })),
            excludeUsers: excludeUserOptions,
        }),
        [userOptions, branchOptions, groupOptions, report, excludeUserOptions]
    )

    // Without the permission you are the only person in the data, so filtering by
    // user, group, or excluded user can only ever narrow to yourself or to
    // nothing -- exclude-users is as meaningless there as the include filter.
    const availableKinds = canViewAllUsers
        ? ['branches', 'users', 'groups', 'actions', 'excludeUsers']
        : ['branches', 'actions']

    // `catalogLoading` covers the REPORT_CATALOG query specifically, which is
    // where `canViewAllUsers` comes from. Folding it into the loading state
    // means the layout choice is only ever made once the catalog has actually
    // answered -- never guessed at its `false` default and then flipped once
    // the real value arrives.
    const isLoading = loading || catalogLoading

    const tabs = useMemo(
        () => [
            // "By X" rather than bare "Incidents" / "Users": these are two
            // lenses on the same activity, not two different subjects, and the
            // old labels read as separate things. Deliberately not "Report by
            // …" -- the whole point of the surrounding copy is that this is an
            // activity log, not a report.
            { id: 'module', name: `By ${report.noun}` },
            { id: 'users', name: 'By Users' },
        ],
        [report]
    )

    return (
        <div>
            <h3 className="reports-panel-title">
                <i className="fa fa-history reports-panel-title-icon" aria-hidden="true"></i>
                {report.name} activity log
            </h3>
            <p className="reports-panel-subtitle">{report.subtitle}</p>

            <div className="reports-panel-tablist" role="tablist" aria-label={`${report.name} report views`}>
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            id={`reports-panel-tab-${reportId}-${tab.id}`}
                            aria-selected={isActive}
                            aria-controls={`reports-panel-tabpanel-${reportId}-${tab.id}`}
                            tabIndex={isActive ? 0 : -1}
                            className={`reports-panel-tab${isActive ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.name}
                        </button>
                    )
                })}
            </div>

            <div
                className="reports-card"
                role="tabpanel"
                id={`reports-panel-tabpanel-${reportId}-${activeTab}`}
                aria-labelledby={`reports-panel-tab-${reportId}-${activeTab}`}
            >
                <ReportToolbar
                    // Remounts on report change: ReportToolbar tracks which filter
                    // tokens are revealed in its own local state, independent of
                    // `filters` itself, so clearing the filter *values* on switch
                    // is not enough to also hide a token that had been opened empty.
                    // A fresh key forces that local state to reset along with it.
                    key={reportId}
                    startDate={range.startDate}
                    endDate={range.endDate}
                    onDateChange={(selection) =>
                        setRange({
                            startDate: moment(selection.startDate).format('YYYY-MM-DD'),
                            endDate: moment(selection.endDate).format('YYYY-MM-DD'),
                        })
                    }
                    filters={filters}
                    onFiltersChange={setFilters}
                    options={options}
                    availableKinds={availableKinds}
                    view={view}
                    onViewChange={setView}
                />

                {isLoading && <p className="reports-status">Loading report...</p>}
                {!isLoading && error && (
                    <p className="reports-status reports-status--error">
                        Unable to load report. Please try again.
                    </p>
                )}
                {!isLoading && !error && (
                    <ReportBody
                        report={report}
                        rows={visibleRows}
                        view={view}
                        layout={activeTab}
                        startDate={range.startDate}
                        endDate={range.endDate}
                        canViewAllUsers={canViewAllUsers}
                    />
                )}
            </div>
        </div>
    )
}

export default ReportsPanel
